import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { selectBreakpointActive } from '@core/stores/breakpoint/breakpoint.selectors';

import { Breakpoint } from '../../../../breakpoints/config';

type BreakpointServiceAsyncPart = {
  [breakpoint in `${Breakpoint}$`]: Observable<boolean>;
};

type BreakpointServiceSyncPart = {
  [breakpoint in Breakpoint]: boolean;
};

type BreakpointServiceSignalPart = {
  [breakpoint in `${Breakpoint}Signal`]: Signal<boolean>;
};

type BreakpointServiceType = BreakpointServiceAsyncPart & BreakpointServiceSyncPart & BreakpointServiceSignalPart;

@Injectable({
  providedIn: 'root',
})
export class BreakpointService implements BreakpointServiceType {
  readonly #store = inject(Store);

  public readonly sm$ = this.isBreakpointActive$('sm');
  public readonly md$ = this.isBreakpointActive$('md');
  public readonly lg$ = this.isBreakpointActive$('lg');
  public readonly xl$ = this.isBreakpointActive$('xl');
  public readonly xxl$ = this.isBreakpointActive$('xxl');

  public readonly smSignal = toSignal<boolean>(this.sm$, {
    requireSync: true,
  });
  public readonly mdSignal = toSignal<boolean>(this.md$, {
    requireSync: true,
  });
  public readonly lgSignal = toSignal<boolean>(this.lg$, {
    requireSync: true,
  });
  public readonly xlSignal = toSignal<boolean>(this.xl$, {
    requireSync: true,
  });
  public readonly xxlSignal = toSignal<boolean>(this.xxl$, {
    requireSync: true,
  });

  public get sm(): boolean {
    return this.smSignal();
  }

  public get md(): boolean {
    return this.mdSignal();
  }

  public get lg(): boolean {
    return this.lgSignal();
  }

  public get xl(): boolean {
    return this.xlSignal();
  }

  public get xxl(): boolean {
    return this.xxlSignal();
  }

  public isBreakpointActive$(breakpoint: Breakpoint): Observable<boolean> {
    return this.#store.select(selectBreakpointActive(breakpoint));
  }
}
